import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { isValidHex } from "@/lib/brandContrast";
import type { Organization } from "@/types/organizations";

interface OrganizationBrandingCardProps {
  organization: Pick<
    Organization,
    "primaryColor" | "secondaryColor" | "accentColor" | "employeeAppBrandingEnabled" | "usesAthlynApp"
  >;
}

interface ColourRowProps {
  label: string;
  value: string | null | undefined;
  testId: string;
  emptyText: string;
}

function ColourRow({ label, value, testId, emptyText }: ColourRowProps) {
  const hex = isValidHex(value) ? value.toUpperCase() : null;
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span data-testid={testId} className="flex items-center gap-2 text-sm font-medium">
        {hex ? (
          <>
            <span
              aria-hidden="true"
              className="inline-block h-4 w-4 rounded border border-border"
              style={{ backgroundColor: hex }}
            />
            {hex}
          </>
        ) : (
          <span className="text-muted-foreground">{emptyText}</span>
        )}
      </span>
    </div>
  );
}

/** Organisation detail (Overview): the app colours and the "Brand the employee app" switch. */
export function OrganizationBrandingCard({ organization }: OrganizationBrandingCardProps) {
  // Branding follows the app package: only companies on the Athlyn app take it.
  const usesAthlynApp = organization.usesAthlynApp === true;
  const on = (organization.employeeAppBrandingEnabled ?? false) && usesAthlynApp;
  return (
    <Card data-testid="org-branding-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">Branding</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <ColourRow label="Main colour" value={organization.primaryColor} testId="org-branding-main" emptyText="Not set" />
        <ColourRow
          label="Highlight colour"
          value={organization.secondaryColor}
          testId="org-branding-highlight"
          emptyText="Not set"
        />
        <ColourRow
          label="Accent colour"
          value={organization.accentColor}
          testId="org-branding-accent"
          emptyText="Derived from highlight"
        />
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Brand the employee app</span>
          <span data-testid="org-branding-enabled" className="flex items-center gap-2">
            <Badge variant={on ? "default" : "secondary"} className="text-xs">
              {on ? "On" : "Off"}
            </Badge>
            {!usesAthlynApp && <span className="text-xs text-muted-foreground">own app</span>}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default OrganizationBrandingCard;
